import { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { io } from 'socket.io-client';
import { jwtDecode } from 'jwt-decode';
import { MapPin } from 'lucide-react';
import TrackingBanner from '../Shared/TrackingBanner';
import TrackingSearchBox from '../Shared/TrackingSearchBox';
import LocationImg from '../../../../assets/Background/location_bg.png';
import ActiveVouchersTable from './sections/ActiveVouchersTable';
import LiveTrackingContext from '../../../../context/tracking/LiveTrackingContext';
import { formatBookingId } from '../../utils/trackingHelpers';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:8000';

const LiveTrackingPage = ({ hideHeader = false }) => {
  const navigate = useNavigate();
  const {
    liveVouchers,
    loading,
    fetchLiveVouchers,
    guestsData,
    loadingGuests,
    fetchGuests,
    pingGuest,
    activeLocationByVoucher,
    updateVoucherLiveLocation
  } = useContext(LiveTrackingContext);

  const [manualTrackingId, setManualTrackingId] = useState('');
  const [expandedVoucherId, setExpandedVoucherId] = useState(null);
  const expandedVoucherIdRef = useRef(null);
  const socketRef = useRef(null);

  useEffect(() => {
    expandedVoucherIdRef.current = expandedVoucherId;
  }, [expandedVoucherId]);

  useEffect(() => {
    fetchLiveVouchers();
  }, [fetchLiveVouchers]);

  useEffect(() => {
    let agentId = localStorage.getItem('agent_id') || null;
    try {
      const token = localStorage.getItem('token_agents');
      if (token) {
        const decoded = jwtDecode(token);
        agentId = decoded?._id || decoded?.id || agentId;
      }
    } catch (err) {
      console.error('Error decoding token in live tracking:', err);
    }

    if (agentId) {
      const socket = io(SERVER_URL);
      if (socket && typeof socket.on === 'function') {
        socketRef.current = socket;

        socket.on('connect', () => {
          socket.emit('join-agent-home', { agentId });
        });

        socket.on('session-started', (data) => {
        const guestName = data.guestName || data.guest_name;
        const eventBookingId = data.booking_id || '';
        const trackingId = data.uniqueTrackingId || '';
        const voucherId = data.voucherId;

        if (guestName) {
          const toastId = `session-start-${trackingId || guestName.toLowerCase()}`;
          const toastMsg = eventBookingId
            ? `${guestName} (${formatBookingId(eventBookingId)}) started sharing location!`
            : `${guestName} started sharing location!`;
          toast.success(toastMsg, { id: toastId });
        }

        // UPDATE ONLY THIS VOUCHER'S LIVE SHARING STATE - DO NOT RELOAD TABLE
        if (voucherId || eventBookingId) {
          updateVoucherLiveLocation(voucherId, {
            activeCount: typeof data.activeCount === 'number' ? data.activeCount : 1,
            lastUpdated: data.timestamp || new Date().toISOString(),
            isActive: true,
            bookingId: eventBookingId
          });

          // Silently refresh guests for this specific voucher row only if currently expanded
          if (
            expandedVoucherIdRef.current &&
            (expandedVoucherIdRef.current === voucherId || expandedVoucherIdRef.current === eventBookingId)
          ) {
            fetchGuests(voucherId || eventBookingId);
          }
        }
      });

        socket.on('sharing-stopped', (data) => {
          const voucherId = data.voucherId;
          const eventBookingId = data.booking_id || '';

          // UPDATE ONLY THIS VOUCHER'S LIVE SHARING STATE - DO NOT RELOAD TABLE
          if (voucherId || eventBookingId) {
            updateVoucherLiveLocation(voucherId, {
              activeCount: typeof data.activeCount === 'number' ? data.activeCount : 0,
              lastUpdated: data.timestamp || new Date().toISOString(),
              isActive: false,
              bookingId: eventBookingId
            });

            if (
              expandedVoucherIdRef.current &&
              (expandedVoucherIdRef.current === voucherId || expandedVoucherIdRef.current === eventBookingId)
            ) {
              fetchGuests(voucherId || eventBookingId);
            }
          }
        });
      }
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [fetchGuests, updateVoucherLiveLocation]);

  // Background fallback ONLY for actively expanded guest list - never reload table
  useEffect(() => {
    if (!expandedVoucherId) return;
    const interval = setInterval(() => {
      fetchGuests(expandedVoucherId);
    }, 25000);
    return () => clearInterval(interval);
  }, [fetchGuests, expandedVoucherId]);

  const toggleRow = (voucherId, bookingId) => {
    if (expandedVoucherId === voucherId) {
      setExpandedVoucherId(null);
    } else {
      setExpandedVoucherId(voucherId);
      fetchGuests(voucherId, bookingId);
    }
  };

  const handlePingUser = async (bookingId, guestName = 'all') => {
    try {
      await pingGuest({
        bookingId,
        guestName,
        message: 'Agent has requested for location. Please start sharing your live location.'
      });
      toast.success(guestName === 'all' ? 'Ping sent to all guests' : `Ping sent to ${guestName}`);
    } catch (err) {
      console.error('Error pinging user:', err);
      toast.error(err.response?.data?.message || 'Failed to send ping');
    }
  };

  const handleManualTrack = (e) => {
    e.preventDefault();
    const query = manualTrackingId.trim();
    if (!query) return;

    const matched = sortedVouchers[0];
    if (matched) {
      navigate(`/tracking/map?voucherId=${matched.bookingID}`);
    } else {
      navigate(`/tracking/map?voucherId=${query}`);
    }
  };

  const filteredVouchers = useMemo(() => {
    const query = manualTrackingId.toLowerCase().trim();
    return liveVouchers.filter((v) => {
      if (!query) {
        return v.status !== 'completed' && v.status !== 'cancelled';
      }

      const matchesClient = v.clientName?.toLowerCase().includes(query);
      const matchesBooking = v.bookingID?.toLowerCase().includes(query);
      const matchesDestination = v.destination?.toLowerCase().includes(query);
      const matchesShareToken = v.shareToken?.toLowerCase().includes(query);
      const matchesActiveGuests = (v.activeGuests || []).some((gName) => gName.toLowerCase().includes(query));

      return matchesClient || matchesBooking || matchesDestination || matchesShareToken || matchesActiveGuests;
    });
  }, [liveVouchers, manualTrackingId]);

  // Promote vouchers with active location sharing
  // Sorting rules:
  // 1. active location sharing first
  // 2. highest active traveller count
  // 3. existing table ordering
  const sortedVouchers = useMemo(() => {
    return [...filteredVouchers].sort((a, b) => {
      const aInfo = activeLocationByVoucher[a._id] || (a.bookingID && activeLocationByVoucher[a.bookingID]);
      const bInfo = activeLocationByVoucher[b._id] || (b.bookingID && activeLocationByVoucher[b.bookingID]);

      const countA = aInfo?.activeCount || 0;
      const countB = bInfo?.activeCount || 0;

      const aActive = countA > 0;
      const bActive = countB > 0;

      if (aActive && !bActive) return -1;
      if (!aActive && bActive) return 1;

      if (aActive && bActive) {
        if (countB !== countA) {
          return countB - countA;
        }
      }

      return 0; // preserve existing table order
    });
  }, [filteredVouchers, activeLocationByVoucher]);

  return (
    <div className="space-y-6 animate-fade-in-up">
      {!hideHeader && (
        <TrackingBanner
          title="Live Location Tracking"
          description="Monitor real-time GPS routes of active tour guests and send push notification alerts to coordinate tours seamlessly."
          image={LocationImg}
        />
      )}

      {/* Search Section */}
      <TrackingSearchBox
        fields={[
          {
            label: 'Booking / Voucher ID',
            placeholder: 'e.g. RAQEC',
            value: manualTrackingId,
            onChange: (e) => setManualTrackingId(e.target.value),
            icon: MapPin
          }
        ]}
        onSubmit={handleManualTrack}
        buttonText="Track Instantly"
        disabled={!manualTrackingId.trim()}
      />

      {/* Active Vouchers Table Section */}
      <ActiveVouchersTable
        vouchers={sortedVouchers}
        activeLocationByVoucher={activeLocationByVoucher}
        loading={loading}
        expandedVoucherId={expandedVoucherId}
        onToggleRow={toggleRow}
        guestsData={guestsData}
        loadingGuests={loadingGuests}
        onPingUser={handlePingUser}
        onRefresh={() => fetchLiveVouchers(true)}
      />
    </div>
  );
};

export default LiveTrackingPage;
